import PatientFilesService, {
  type IPatientFilesFilter,
  type TFileWithType,
} from "@/api/models/patient/patientFiles";
import type { TFile } from "@/api/shared/file";
import { useQueryClient } from "@tanstack/vue-query";
import {
  computed,
  inject,
  toValue,
  provide,
  readonly,
  toRef,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";
import { EPatientFileTypeFilter } from "@/api/models/patient/patientFiles"; // adjust path if needed
import { createQueryKeys, useGenericListQuery } from "@/composables/shared/queries";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import type { TCompositeView } from "@/api/models/patient/compositeView";
import { groupByCreatedDate } from "@/utils/groupObject.ts";

export const PatientFilesQuerykey = createQueryKeys(PatientFilesService.queryKey, (key) => ({
  listOfPatientFiles: key<IPatientFilesFilter>("list"),
}));

/** Scans, xrays, photos, supporting documents, and composite views — default for patient file lists in chat, cards, etc. */
export const DEFAULT_PATIENT_FILE_TYPES = [
  EPatientFileTypeFilter.PATIENT_SCAN,
  EPatientFileTypeFilter.PATIENT_SCAN_EXTERNAL,
  EPatientFileTypeFilter.PATIENT_XRAY,
  EPatientFileTypeFilter.PATIENT_PHOTO,
  EPatientFileTypeFilter.PATIENT_SUPPORTING_DOCUMENT,
  EPatientFileTypeFilter.COMPOSITE_VIEW_GENERATED,
  EPatientFileTypeFilter.COMPOSITE_VIEW,
] as const satisfies readonly EPatientFileTypeFilter[];

export function usePatientFiles(
  filters: MaybeRefOrGetter<Partial<IPatientFilesFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) {
  return useGenericListQuery<TFileWithType, Partial<IPatientFilesFilter>>({
    queryKey: PatientFilesQuerykey.listOfPatientFiles(),
    params: { filters, enabled: computed(() => toValue(enabled)) },
    fetcher: async (filters) => {
      return PatientFilesService.getAll({ patientId: filters?.patient_id! }, filters ?? {});
    },
    permission: $permSubjects.FILE__PATIENTFILE,
    neededKey: ["patient_id"],
  });
}

type TUseCachedPatientFilesOptions = {
  patientId: MaybeRefOrGetter<string | undefined>;
  wantedFileTypes?: EPatientFileTypeFilter[];
};

export function useCachedPatientFiles(options: TUseCachedPatientFilesOptions) {
  const queryClient = useQueryClient();

  const wantedFileTypes = computed<EPatientFileTypeFilter[]>(
    () => options.wantedFileTypes ?? [...DEFAULT_PATIENT_FILE_TYPES]
  );

  const wantedTypeSet = computed(() => new Set(wantedFileTypes.value));
  const patientIdRef = toRef(options.patientId);

  const cachedPatientFiles = computed<TFileWithType[]>(() => {
    const nextPatientId = patientIdRef.value;
    if (!nextPatientId) return [];

    const cachedQueries = queryClient.getQueriesData<TFileWithType[]>({
      queryKey: PatientFilesQuerykey.listOfPatientFiles({ patient_id: nextPatientId }),
    });

    const fileById = new Map<string, TFileWithType>();

    for (const [, queryData] of cachedQueries) {
      if (!queryData?.length) continue;

      for (const file of queryData) {
        if (!file?.id || !file.file_type || !wantedTypeSet.value.has(file.file_type)) continue;
        fileById.set(String(file.id), file);
      }
    }

    return Array.from(fileById.values());
  });

  const shouldFetchPatientFiles = computed(
    () => !!patientIdRef.value && cachedPatientFiles.value.length === 0
  );

  const { data: fetchedPatientFiles, isLoading: isFetchingPatientFiles } = usePatientFiles(
    computed(() => ({
      patient_id: patientIdRef.value as string,
      file_type__in: wantedFileTypes.value,
    })),
    computed(() => shouldFetchPatientFiles.value)
  );

  const patientFiles = computed<TFileWithType[]>(() =>
    cachedPatientFiles.value.length > 0
      ? cachedPatientFiles.value
      : (fetchedPatientFiles.value ?? [])
  );

  const isLoading = computed(() => shouldFetchPatientFiles.value && isFetchingPatientFiles.value);

  return {
    patientFiles,
    cachedPatientFiles,
    fetchedPatientFiles,
    isLoading,
  };
}

export enum EPatientFileType {
  SCAN = "ioScan",
  XRAY = "xRay",
  PHOTO = "photo",
  SUPPORTING_DOCUMENT = "supportingDocument",
  COMPOSITE_VIEW = "compositeView",
}

/**
 * Extensions the supporting-documents upload fields accept — the only patient
 * file group that takes documents alongside images.
 *
 * Spelled out as extensions rather than reusing `EFileType.DOCUMENT` because
 * that bucket also covers ppt/pptx, and because `getFileType` reports a picked
 * `.pdf` as APPLICATION, so a type-bucket check would reject PDFs outright.
 */
export const SUPPORTING_DOCUMENT_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "svg",
  "webp",
  "heic",
  "heif",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
];

export const useUploadPatientPhoto = (
  patientId: MaybeRefOrGetter<string | null | undefined>,
  type: MaybeRefOrGetter<EPatientFileType | undefined>
) => {
  const patientIdRef = toRef(patientId);
  const typeRef = toRef(type);
  return useGenericMutation<TFile, TFile>({
    mutationFn: (file) => {
      const id = patientIdRef.value;
      const currentType = typeRef.value;

      if (!id) throw new Error("No patient ID provided");
      if (!currentType) throw new Error("No upload type provided");

      switch (currentType) {
        case EPatientFileType.PHOTO:
          return PatientFilesService.uploadPhoto({ patientId: id }, file);
        case EPatientFileType.SCAN:
          return PatientFilesService.uploadScan({ patientId: id }, file);
        case EPatientFileType.XRAY:
          return PatientFilesService.uploadXray({ patientId: id }, file);
        case EPatientFileType.SUPPORTING_DOCUMENT:
          return PatientFilesService.uploadSupportingDocument({ patientId: id }, file);
        default:
          return PatientFilesService.uploadPhoto({ patientId: id }, file);
      }
    },
    feature: "patientFiles.upload",
    invalidates: () => {
      const id = patientIdRef.value;
      return id ? [PatientFilesQuerykey.listOfPatientFiles({ patient_id: id })] : [];
    },
  });
};

export const useDeletePatientFile = (patientId: MaybeRefOrGetter<string | null | undefined>) => {
  const patientIdRef = toRef(patientId);
  return useGenericMutation<void, string>({
    mutationFn: (fileId) => {
      const id = patientIdRef.value;
      if (!id) throw new Error("No patient ID provided");

      return PatientFilesService.remove({ patientId: id, id: fileId });
    },
    feature: "patientFiles.delete",
    invalidates: () => {
      const id = patientIdRef.value;
      return id ? [PatientFilesQuerykey.listOfPatientFiles({ patient_id: id })] : [];
    },
  });
};

type TPatientProvidedFile = TFileWithType;
type TPatientFileCollection =
  | Readonly<Ref<TPatientProvidedFile[]>>
  | ComputedRef<TPatientProvidedFile[]>;

type TPatientFileBucket =
  | "photos"
  | "scans"
  | "xrays"
  | "supportingDocuments"
  | "compositeGenerated"
  | "compositePicture";

type TPatientFilesContext = Partial<Record<TPatientFileBucket, TPatientFileCollection>>;

const patientFilesContextKey: InjectionKey<TPatientFilesContext> = Symbol("patientFilesContext");
const emptyFiles = computed<TPatientProvidedFile[]>(() => []);

function toReadonlyFileCollection(
  files: Ref<TPatientProvidedFile[]> | ComputedRef<TPatientProvidedFile[]>
): TPatientFileCollection {
  return readonly(files) as TPatientFileCollection;
}

/**
 * Provide any combination of patient file buckets.
 *
 * Example:
 * providePatientFiles({
 *   photos: patientPhotoFiles,
 *   xrays: patientXrayFiles,
 * });
 */
export function providePatientFiles(files: TPatientFilesContext) {
  provide(patientFilesContextKey, {
    photos: files.photos ? toReadonlyFileCollection(files.photos) : undefined,
    scans: files.scans ? toReadonlyFileCollection(files.scans) : undefined,
    xrays: files.xrays ? toReadonlyFileCollection(files.xrays) : undefined,
    supportingDocuments: files.supportingDocuments
      ? toReadonlyFileCollection(files.supportingDocuments)
      : undefined,
    compositeGenerated: files.compositeGenerated
      ? toReadonlyFileCollection(files.compositeGenerated)
      : undefined,
    compositePicture: files.compositePicture
      ? toReadonlyFileCollection(files.compositePicture)
      : undefined,
  });
}

/**
 * Consume provided patient files.
 * Missing buckets fall back to empty computed arrays.
 */
export function usePatientProvidedFiles() {
  const ctx = inject(patientFilesContextKey, {});

  return {
    photos: ctx.photos ?? emptyFiles,
    scans: ctx.scans ?? emptyFiles,
    xrays: ctx.xrays ?? emptyFiles,
    supportingDocuments: ctx.supportingDocuments ?? emptyFiles,
    compositeGenerated: ctx.compositeGenerated ?? emptyFiles,
    compositePicture: ctx.compositePicture ?? emptyFiles,
  };
}

/**
 * Group already-fetched patient files by bucket.
 */
export function useGroupedPatientFiles(
  files: MaybeRefOrGetter<TPatientProvidedFile[] | undefined>
) {
  const filesRef = toRef(files);

  const photos = computed<TPatientProvidedFile[]>(
    () =>
      filesRef.value?.filter((file) => file.file_type === EPatientFileTypeFilter.PATIENT_PHOTO) ??
      []
  );

  const scans = computed<TPatientProvidedFile[]>(
    () =>
      filesRef.value?.filter(
        (file) =>
          file.file_type === EPatientFileTypeFilter.PATIENT_SCAN ||
          file.file_type === EPatientFileTypeFilter.PATIENT_SCAN_EXTERNAL
      ) ?? []
  );

  const xrays = computed<TPatientProvidedFile[]>(
    () =>
      filesRef.value?.filter((file) => file.file_type === EPatientFileTypeFilter.PATIENT_XRAY) ?? []
  );

  const supportingDocuments = computed<TPatientProvidedFile[]>(
    () =>
      filesRef.value?.filter(
        (file) => file.file_type === EPatientFileTypeFilter.PATIENT_SUPPORTING_DOCUMENT
      ) ?? []
  );

  const compositeGenerated = computed<TPatientProvidedFile[]>(
    () =>
      filesRef.value?.filter(
        (file) => file.file_type === EPatientFileTypeFilter.COMPOSITE_VIEW_GENERATED
      ) ?? []
  );

  const compositePicture = computed<TPatientProvidedFile[]>(
    () =>
      filesRef.value?.filter((file) => file.file_type === EPatientFileTypeFilter.COMPOSITE_VIEW) ??
      []
  );

  return {
    photos,
    scans,
    xrays,
    supportingDocuments,
    compositeGenerated,
    compositePicture,
  };
}

/**
 * Group + provide in one step.
 */
export function provideGroupedPatientFiles(
  files: MaybeRefOrGetter<TPatientProvidedFile[] | undefined>
) {
  const grouped = useGroupedPatientFiles(files);

  providePatientFiles({
    photos: grouped.photos,
    scans: grouped.scans,
    xrays: grouped.xrays,
    supportingDocuments: grouped.supportingDocuments,
    compositeGenerated: grouped.compositeGenerated,
    compositePicture: grouped.compositePicture,
  });

  return grouped;
}

type TCompositeViewCollection = Readonly<Ref<TCompositeView[]>> | ComputedRef<TCompositeView[]>;

const compositeViewsContextKey: InjectionKey<TCompositeViewCollection> =
  Symbol("compositeViewsContext");

const emptyCompositeViews = computed<TCompositeView[]>(() => []);

function toReadonlyCompositeCollection(
  views: Ref<TCompositeView[]> | ComputedRef<TCompositeView[]>
): TCompositeViewCollection {
  return readonly(views) as TCompositeViewCollection;
}

/**
 * Provide composite views separately from patient file buckets.
 */
export function provideCompositeViews(
  views: Ref<TCompositeView[]> | ComputedRef<TCompositeView[]>
) {
  provide(compositeViewsContextKey, toReadonlyCompositeCollection(views));
}

/**
 * Consume provided composite views.
 * Falls back to empty computed array when not provided.
 */
export function useProvidedCompositeViews() {
  return inject(compositeViewsContextKey, emptyCompositeViews);
}

export function useGroupByDateProvidedCompositeViews() {
  const compositeViews = useProvidedCompositeViews();

  return computed(() => groupByCreatedDate(compositeViews.value));
}

export function usePatientFilesGroupedByDate() {
  const provided = usePatientProvidedFiles();

  const photos = computed(() => groupByCreatedDate(provided.photos.value));
  const scans = computed(() => groupByCreatedDate(provided.scans.value));
  const xrays = computed(() => groupByCreatedDate(provided.xrays.value));
  const supportingDocuments = computed(() =>
    groupByCreatedDate(provided.supportingDocuments.value)
  );
  const compositeGenerated = computed(() => groupByCreatedDate(provided.compositeGenerated.value));
  const compositePicture = computed(() => groupByCreatedDate(provided.compositePicture.value));

  return {
    photos,
    scans,
    xrays,
    supportingDocuments,
    compositeGenerated,
    compositePicture,
  };
}
